import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import isoWeek from 'dayjs/plugin/isoWeek'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isBetween)
dayjs.extend(isoWeek)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

/**
 * The clubs' timezone, and dayjs's default for the whole app. Import this
 * wherever a day boundary has to be the club's rather than the viewer's
 * browser zone, instead of re-declaring the literal.
 */
export const CLUB_TIMEZONE = 'Asia/Bangkok'

dayjs.tz.setDefault(CLUB_TIMEZONE)

export const parseToDayjs = (value: unknown) => {
  if (!value) return null
  if (dayjs.isDayjs(value)) return value
  const parsed = dayjs(value as string | number | Date)
  return parsed.isValid() ? parsed : null
}

export const getUpcomingSessions = (
  schedule: {
    enabled: boolean
    startTime: string
    endTime: string
  }[],
  sessionCount = 5,
  now = dayjs(),
  daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
) => {
  const enabledDays = daysOrder
    .map((day, i) => ({ day, index: i, ...schedule[i] }))
    .filter((d) => d.enabled)

  if (enabledDays.length === 0) return []

  const sessions: { date: string; start: string; end: string }[] = []

  // scan enough days to cover at least N cycles of 7 days
  const limit = Math.ceil((sessionCount / enabledDays.length) * 7) + 7

  for (let i = 0; sessions.length < sessionCount && i < limit; i++) {
    const date = now.add(i, 'day')
    const weekday = date.day()
    const match = enabledDays.find((d) => d.index === weekday)
    if (!match) continue

    const start = dayjs(`${date.format('YYYY-MM-DD')} ${match.startTime}`, 'YYYY-MM-DD HH.mm')
    const end = dayjs(`${date.format('YYYY-MM-DD')} ${match.endTime}`, 'YYYY-MM-DD HH.mm')

    // only include future sessions
    if (end.isAfter(now)) {
      sessions.push({
        date: start.format('ddd, D MMM YYYY'),
        start: start.format('HH:mm'),
        end: end.format('HH:mm'),
      })
    }
  }

  return sessions.slice(0, sessionCount)
}

export default dayjs
export type { Dayjs } from 'dayjs'
